import React, { useMemo, useState } from 'react'

import { Button, ButtonColors, ButtonVariants } from '@inno-ui/button'
import {
  ArrowRightRectangleLineIcon,
  EnvelopeLineIcon,
  IconSizes,
  LaptopLineIcon,
  LockLineIcon,
  StarShieldLineIcon
} from '@inno-ui/icons'
import { TextInput } from '@inno-ui/text-input'
import { Toggle, ToggleSizes } from '@inno-ui/toggle'
import { DisableTwoFactorModal } from 'modules/auth/components/DisableTwoFactorModal/DisableTwoFactorModal'
import { EnableTwoFactorModal } from 'modules/auth/components/EnableTwoFactorModal/EnableTwoFactorModal'
import { useTranslation } from 'react-i18next'
import { useDeleteRefreshToken } from 'shared/api/auth/requests/deviceManagement/deleteRefreshToken'
import { useGetRefreshTokens } from 'shared/api/auth/requests/deviceManagement/getRefreshTokens'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { useUserContext } from 'shared/hooks/useUserContext'
import { persistTwoFaChecked } from 'shared/utils'
import { lsRemoveItem } from 'shared/utils/local-storage'

import { EditProfileSection } from '../EditProfileSection'
import { DeviceItem } from '../LinkedDevices'
import linkedDevicesStyles from '../LinkedDevices/LinkedDevices.module.scss'
import styles from './EditProfileLayer.module.scss'

interface Props {
  onNavigateToChangePassword: () => void
}

const EditProfileLayer: React.FC<Props> = ({ onNavigateToChangePassword }) => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'my_profile_page'
  })
  const { user } = useUserContext()

  const [twoFactorAuthState, setTwoFactorAuthState] = useState<{
    isOpen: boolean
    type: 'enable' | 'disable'
  }>({ isOpen: false, type: user?.twofa_checked ? 'enable' : 'disable' })

  const handleShowTwoFactorAuthModal = (
    isOpen: boolean,
    type?: 'enable' | 'disable'
  ) => {
    setTwoFactorAuthState(prev => ({
      ...prev,
      isOpen,
      type: type || prev.type
    }))
  }

  const {
    query: { data: refreshTokenData }
  } = useGetRefreshTokens({ userId: user?.id })

  const {
    mutation: {
      mutate: deleteRefreshTokenMutate,
      isPending: deleteTokenLoading
    }
  } = useDeleteRefreshToken()

  const [currentDevice, ...otherDevices] = useMemo(
    () =>
      refreshTokenData?.sort(a => (a.token === user?.refresh_token ? -1 : 1)) ||
      [],
    [refreshTokenData, user?.refresh_token]
  )

  const handleLogoutFromAll = () => {
    deleteRefreshTokenMutate(
      {},
      {
        onSuccess: () => {
          lsRemoveItem(LocalStorageKey.USER)
          window.location.reload()
        }
      }
    )
  }

  return (
    <div className={styles.EditProfileLayer}>
      <EditProfileSection
        title={t('email')}
        icon={<EnvelopeLineIcon size={IconSizes.Size34} />}
      >
        <TextInput readOnly value={user?.email} />
      </EditProfileSection>
      <EditProfileSection
        icon={<LockLineIcon size={IconSizes.Size34} />}
        title={t('password_and_login')}
      >
        <Button
          variant={ButtonVariants.Light}
          label={t('change_password_button')}
          onClick={onNavigateToChangePassword}
        />
      </EditProfileSection>
      <EditProfileSection
        icon={<StarShieldLineIcon size={IconSizes.Size34} />}
        title={t('two_factor')}
        direction='horizontal'
      >
        <p>
          <strong>{t('two_factor')}</strong>
          <br />
          {t('activate_two_factor')}
        </p>
        <Toggle
          size={ToggleSizes.S}
          checked={
            user?.twofa_checked === 1 || twoFactorAuthState.type === 'enable'
          }
          onChange={checked =>
            handleShowTwoFactorAuthModal(true, checked ? 'enable' : 'disable')
          }
        />
        <EnableTwoFactorModal
          isOpen={
            twoFactorAuthState.isOpen && twoFactorAuthState.type === 'enable'
          }
          onCancel={() =>
            setTwoFactorAuthState({ isOpen: false, type: 'disable' })
          }
          setIsOpen={isOpen => handleShowTwoFactorAuthModal(isOpen, 'enable')}
          onSuccess={() => {
            persistTwoFaChecked(user, 1)
            setTwoFactorAuthState({ isOpen: false, type: 'enable' })
          }}
        />
        <DisableTwoFactorModal
          isOpen={
            twoFactorAuthState.isOpen && twoFactorAuthState.type === 'disable'
          }
          setIsOpen={isOpen => handleShowTwoFactorAuthModal(isOpen, 'disable')}
          onSuccess={() => {
            persistTwoFaChecked(user, 0)
            setTwoFactorAuthState({ isOpen: false, type: 'disable' })
          }}
          onCancel={() =>
            setTwoFactorAuthState({ isOpen: false, type: 'enable' })
          }
        />
      </EditProfileSection>
      <EditProfileSection
        icon={<LaptopLineIcon size={IconSizes.Size34} />}
        title={t('linked_devices')}
        appendTitle={
          <Button
            variant={ButtonVariants.Text}
            icon={ArrowRightRectangleLineIcon}
            color={ButtonColors.Red}
            onClick={handleLogoutFromAll}
            label={t('logout_from_all')}
          />
        }
      >
        <div className={linkedDevicesStyles.devicesWrapper}>
          <span className={linkedDevicesStyles.header}>
            {t('current_device')}
          </span>
          <div className={linkedDevicesStyles.items}>
            {currentDevice && (
              <DeviceItem
                device={currentDevice}
                isCurrent={true}
                isDeleting={deleteTokenLoading}
              />
            )}
          </div>
        </div>

        {!!otherDevices.length && (
          <div className={linkedDevicesStyles.devicesWrapper}>
            <span className={linkedDevicesStyles.header}>
              {t('other_devices')}
            </span>
            <div className={linkedDevicesStyles.items}>
              {otherDevices.map(device => (
                <DeviceItem
                  key={device.id}
                  device={device}
                  isCurrent={false}
                  onDelete={(id: number) => deleteRefreshTokenMutate({ id })}
                  isDeleting={deleteTokenLoading}
                />
              ))}
            </div>
          </div>
        )}
      </EditProfileSection>
    </div>
  )
}

export { EditProfileLayer }
